import React, { useState, useEffect } from 'react';
import ModalComponent from '../../common/Modal/modal';
import { CustomButton } from '../../../widgets';
import { getActiveStyle, generateBibliography, getCachedStyleXML } from '../services/CitationService/CitationStyleService';
import './ReferenceManager.scss';

/**
 * CitationPicker
 *
 * Let the user select one or more references from their library to insert
 * as a citation group at the current cursor position.
 */
const CitationPicker = ({ visible, references, onSelect, onCancel }) => {
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [activeStyle, setActiveStyle] = useState(getActiveStyle());

  useEffect(() => {
    if (!visible) {
      setSelectedRefs([]);
    } else {
      setActiveStyle(getActiveStyle());
    }
  }, [visible]);

  useEffect(() => {
    const handleStyleChange = (e) => {
      setActiveStyle(e.detail || getActiveStyle());
    };
    window.addEventListener('citationStyleChanged', handleStyleChange);
    return () => window.removeEventListener('citationStyleChanged', handleStyleChange);
  }, []);

  const handleToggleReference = (ref) => {
    setSelectedRefs((prev) => {
      const exists = prev.find((r) => r.id === ref.id);
      if (exists) {
        return prev.filter((r) => r.id !== ref.id);
      } else {
        return [...prev, ref];
      }
    });
  };

  const handleInsert = () => {
    if (selectedRefs.length > 0) {
      onSelect(selectedRefs);
      setSelectedRefs([]);
    }
  };

  // ── Prepare styled previews via CSL ──────────────────────────────────────
  const styleXML = getCachedStyleXML(activeStyle.id);
  const cslResults = generateBibliography(references, styleXML);

  const formatReferenceFallback = (ref) => {
    const { type, authors, title, year, journal, volume, pages, publisher, url } = ref;
    switch (type) {
      case 'article':
        return `${authors} (${year}). ${title}. ${journal}, ${volume}${pages ? `, ${pages}` : ''}.`;
      case 'book':
        return `${authors} (${year}). ${title}. ${publisher}.`;
      case 'website':
        return `${authors} (${year}). ${title}. Retrieved from ${url}`;
      case 'conference':
        return `${authors} (${year}). ${title}. In ${journal}${pages ? ` (pp. ${pages})` : ''}.`;
      default:
        return `${authors} (${year}). ${title}.`;
    }
  };

  return (
    <ModalComponent
      header="Select Citations"
      isOpen={visible}
      onClose={onCancel}
      width="700px"
      customClass="citation-picker-modal"
      footer={
        <div className="d-flex justify-content-end gap-2 pr-4 pb-3">
          <CustomButton type="secondary" text="Cancel" onClick={onCancel} />
          <CustomButton
            type="primary"
            text={`Insert Citation${selectedRefs.length > 0 ? `s (${selectedRefs.length})` : ''}`}
            onClick={handleInsert}
            disabled={selectedRefs.length === 0}
          />
        </div>
      }
      body={
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }} className="p-3">
          {references.map((ref, index) => {
            const isSelected = selectedRefs.find((r) => r.id === ref.id);
            // If we have CSL output, use it; otherwise fallback to simple text
            const styledHtml = cslResults?.entries?.[index];

            return (
              <div
                key={ref.id}
                className={`reference-item ${isSelected ? 'selected' : ''} mb-3 p-2 border rounded cursor-pointer d-flex align-items-start`}
                onClick={() => handleToggleReference(ref)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#f5faff' : '#ffffff',
                  borderColor: isSelected ? 'var(--primary-color, #006BD9)' : '#e0e0e0',
                  boxShadow: isSelected ? '0 0 0 1px var(--primary-color, #006BD9)' : 'none',
                  transition: 'background 0.2s, border-color 0.2s',
                }}
              >
                <div className="mr-3" style={{ marginRight: '16px', paddingTop: '4px' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/${isSelected ? 'checkbox_selected' : 'checkbox_idle'}.svg`}
                    alt="checkbox"
                    width={20}
                    height={20}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  {styledHtml ? (
                    <div
                      className="reference-text"
                      dangerouslySetInnerHTML={{ __html: styledHtml }}
                    />
                  ) : (
                    <div>
                      <span className="reference-number font-weight-bold mr-2" style={{ marginRight: '8px', color: 'var(--primary-color, #006BD9)' }}>
                        [{ref.number}]
                      </span>
                      <span className="reference-text">{formatReferenceFallback(ref)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      }
    />
  );
};

export default CitationPicker;
